import { BarChart3, Bell, Bus, ClipboardList, Database, FileText, HelpCircle, Home, LayoutDashboard, LogOut, Map, Menu, Moon, ScrollText, Settings, Shield, Sun, Utensils, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import { adminCapabilities } from '../auth/permissions'
import { useAdminTheme } from '../state/ThemeProvider'
import PwaBanner from './PwaBanner'
import { prefetchAdminAnalytics } from '../lib/analytics'
import { fetchGrowth } from '../lib/growth'
import { formatReportBadgeCount, useReportTriage } from '../lib/reportTriageStore'

const NAV_GROUPS = [
  {
    label: '운영 홈',
    items: [
      { to: '/dashboard', label: '운영 홈', Icon: LayoutDashboard },
    ],
  },
  {
    label: '분석',
    items: [
      { to: '/analytics', label: '사용자 분석', Icon: BarChart3 },
      { to: '/growth', label: '성장 분석', Icon: BarChart3 },
    ],
  },
  {
    label: '운영',
    items: [
      { to: '/reports', label: '제보', Icon: ClipboardList },
      { to: '/notifications', label: '푸시 알림', Icon: Bell },
      { to: '/audit', label: '작업 기록', Icon: ScrollText },
    ],
  },
  {
    label: '콘텐츠',
    items: [
      { to: '/announcements', label: '앱 내 안내', Icon: FileText },
      { to: '/home-config', label: '홈 화면', Icon: Home },
      { to: '/faq', label: '자주 묻는 질문', Icon: HelpCircle },
      { to: '/cafeteria-notice', label: '학식·학교 공지', Icon: Utensils },
    ],
  },
  {
    label: '서비스',
    items: [
      { to: '/shuttle', label: '셔틀', Icon: Bus },
      { to: '/map', label: '캠퍼스 지도', Icon: Map },
    ],
  },
  {
    label: '시스템',
    items: [
      { to: '/feature-settings', label: '서비스 설정', Icon: Settings },
      { to: '/data-versions', label: '데이터 배포', Icon: Database },
      { to: '/admin-roles', label: '관리자 관리', Icon: Shield },
    ],
  },
]
const NAV_ITEMS = NAV_GROUPS.flatMap(group => group.items)

function NavItems({ onNavigate, grouped = false }) {
  const { role } = useAdminAuth()
  const { canViewAdminRoles } = adminCapabilities(role)
  const { newCount, countReady, countError } = useReportTriage()
  const newReportCount = countReady && !countError ? newCount : 0
  const reportBadge = newReportCount > 0 ? formatReportBadgeCount(newReportCount) : null
  const prefetch = to => {
    if (to === '/analytics') return prefetchAdminAnalytics({ period: 30, details: true }).catch(() => null)
    if (to === '/growth') return fetchGrowth(30).catch(() => null)
    return null
  }
  const linkProps = to => ({ onMouseEnter: () => { window.setTimeout(() => prefetch(to), 120) }, onFocus: () => prefetch(to) })
  if (!grouped) {
    return NAV_ITEMS.filter(item => item.to !== '/admin-roles' || canViewAdminRoles).map(({ to, label, Icon }) => (
      <NavLink key={to} to={to} onClick={onNavigate} {...linkProps(to)} aria-label={to === '/reports' && reportBadge ? `제보, 신규 제보 ${newReportCount}건` : undefined} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
        <Icon size={19} strokeWidth={2} /><span>{label}</span>{to === '/reports' && reportBadge && <span className="nav-badge" aria-hidden="true">{reportBadge}</span>}
      </NavLink>
    ))
  }
  return NAV_GROUPS.map(group => {
    const items = group.items.filter(item => item.to !== '/admin-roles' || canViewAdminRoles)
    if (!items.length) return null
    return (
    <div className="nav-group" key={group.label}>
      <p className="nav-group-label">{group.label}</p>
      {items.map(({ to, label, Icon }) => (
        <NavLink key={to} to={to} onClick={onNavigate} {...linkProps(to)} aria-label={to === '/reports' && reportBadge ? `제보, 신규 제보 ${newReportCount}건` : undefined} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
          <Icon size={19} strokeWidth={2} /><span>{label}</span>{to === '/reports' && reportBadge && <span className="nav-badge" aria-hidden="true">{reportBadge}</span>}
        </NavLink>
      ))}
    </div>
    )
  })
}

function MobileMonitorNav() {
  return <nav className="monitor-bottom-nav" aria-label="모바일 모니터 메뉴"><NavLink to="/dashboard" end className={({ isActive }) => `monitor-nav-link ${isActive ? 'active' : ''}`}><LayoutDashboard size={18} /><span>오늘</span></NavLink><NavLink to="/analytics" className={({ isActive }) => `monitor-nav-link ${isActive ? 'active' : ''}`}><BarChart3 size={18} /><span>사용자</span></NavLink><NavLink to="/monitor/features" className={({ isActive }) => `monitor-nav-link ${isActive ? 'active' : ''}`}><BarChart3 size={18} /><span>기능</span></NavLink></nav>
}

export default function AdminLayout({ children, monitor = false }) {
  const auth = useAdminAuth()
  const { isViewer } = adminCapabilities(auth.role)
  const { isDark, setTheme } = useAdminTheme()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()
  useEffect(() => setDrawerOpen(false), [location.pathname])

  if (monitor) return <div className="monitor-shell"><header className="monitor-shell-topbar"><button className="icon-button" type="button" onClick={auth.logout} aria-label="로그아웃"><LogOut size={17} /></button><span>READ-ONLY MONITOR</span><button className="icon-button" type="button" onClick={() => setTheme(isDark ? 'light' : 'dark')} aria-label={isDark ? '라이트 모드' : '다크 모드'}>{isDark ? <Sun size={17} /> : <Moon size={17} />}</button></header><PwaBanner isDashboard={location.pathname === '/dashboard'} isMonitor /><main className="monitor-content">{children}</main><MobileMonitorNav /></div>

  return (
    <div className="admin-shell">
      <aside className="sidebar">
        <div className="admin-brand"><span className="brand-symbol">S</span><div><strong>SU-Guide</strong><small>{isViewer ? 'VIEWER · 읽기 전용' : 'Owner 운영 콘솔'}</small></div></div>
        <nav className="sidebar-nav"><NavItems grouped /></nav>
        <div className="sidebar-account">
          <span className="avatar">{(auth.user?.displayName || auth.user?.email || 'A').slice(0, 1).toUpperCase()}</span>
          <div><strong>{auth.user?.displayName || '관리자'}</strong><small>{isViewer ? 'VIEWER · 읽기 전용' : auth.user?.email}</small></div>
          <button className="icon-button" type="button" onClick={auth.logout} aria-label="로그아웃"><LogOut size={18} /></button>
        </div>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <button className="icon-button mobile-menu" type="button" onClick={() => setDrawerOpen(true)} aria-label="메뉴 열기"><Menu size={22} /></button>
          <div className="topbar-title"><strong>운영 콘솔</strong><span>현재 서비스</span></div>
          <div className="topbar-actions">
            <button className="icon-button" type="button" onClick={() => setTheme(isDark ? 'light' : 'dark')} aria-label={isDark ? '라이트 모드' : '다크 모드'}>
              {isDark ? <Sun size={19} /> : <Moon size={19} />}
            </button>
            <button className="account-button" type="button" onClick={auth.logout} aria-label="로그아웃"><span className="avatar small">{(auth.user?.displayName || 'A').slice(0, 1)}</span></button>
          </div>
        </header>
        <PwaBanner isDashboard={location.pathname === '/dashboard'} />
        <main className="content">{children}</main>
      </div>

      <nav className="bottom-nav"><NavItems /></nav>
      {drawerOpen && <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} role="presentation" />}
      <aside className={`mobile-drawer ${drawerOpen ? 'open' : ''}`} aria-hidden={!drawerOpen}>
        <div className="drawer-header"><div className="admin-brand"><span className="brand-symbol">S</span><strong>SU-Guide Admin</strong></div><button className="icon-button" type="button" onClick={() => setDrawerOpen(false)} aria-label="메뉴 닫기"><X /></button></div>
        <nav><NavItems grouped onNavigate={() => setDrawerOpen(false)} /></nav>
      </aside>
    </div>
  )
}
